<template>
  <div class="min-h-screen bg-gray-900">
    <!-- 导航栏 -->
    <nav class="nav-container">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center h-16">
          <button @click="goBack" class="mr-4 text-gray-400 hover:text-white transition-colors flex-shrink-0">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <div class="flex-1 flex justify-center">
            <div class="max-w-2xl w-full">
              <form @submit.prevent="handleSearch" class="flex gap-2">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索影视资源..."
                  class="search-input flex-1"
                />
                <button
                  type="submit"
                  class="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200 active:bg-blue-800"
                >
                  搜索
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- 搜索结果 -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- 搜索统计 -->
      <div v-if="!loading && searchQuery" class="mb-6">
        <p class="text-gray-400">
          搜索 "{{ searchQuery }}" 找到 {{ totalResults }} 个结果
        </p>
      </div>

      <!-- 网盘类型筛选 -->
      <div v-if="!loading && searchQuery" class="mb-6">
        <div class="flex items-center mb-3">
          <svg class="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
          </svg>
          <span class="text-gray-300 text-sm font-medium">网盘类型</span>
          <span class="ml-2 text-xs text-gray-500">（点击切换）</span>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            @click="handleDiskTypeSelect('')"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2',
              selectedDiskType === '' 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/50' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
            ]"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z"/>
            </svg>
            全部
          </button>
          <button
            @click="handleDiskTypeSelect('quark')"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2',
              selectedDiskType === 'quark' 
                ? 'bg-[#8B5CF6] text-white shadow-lg shadow-[#8B5CF6]/50' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700 hover:border-[#8B5CF6]/50'
            ]"
          >
            <img src="/favicon-quark.ico" alt="夸克" class="w-4 h-4" />
            夸克网盘
          </button>
          <button
            @click="handleDiskTypeSelect('baidu')"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2',
              selectedDiskType === 'baidu' 
                ? 'bg-[#06A7FF] text-white shadow-lg shadow-[#06A7FF]/50' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700 hover:border-[#06A7FF]/50'
            ]"
          >
            <img src="/favicon-baidu.ico" alt="百度" class="w-4 h-4" />
            百度网盘
          </button>
        </div>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="text-gray-400 mt-2">搜索中...</p>
      </div>

      <!-- 错误信息 -->
      <div v-else-if="error" class="text-center py-8">
        <svg class="w-16 h-16 text-red-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-red-500 mt-4">{{ error }}</p>
      </div>

      <!-- 搜索结果列表 -->
      <div v-else-if="results.length > 0" class="space-y-4">
        <div
          v-for="result in results"
          :key="result.source"
          class="search-result bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors"
        >
          <div class="flex justify-between items-start mb-3 gap-4">
            <h3 class="search-result-title flex-1 min-w-0 break-words text-white font-medium">{{ result.title }}</h3>
            <span class="px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 flex items-center gap-1.5"
              :class="{
                'bg-[#8B5CF6] text-white': result.disk_type === 'quark',
                'bg-[#06A7FF] text-white': result.disk_type === 'baidu',
                'bg-orange-500 text-white': result.disk_type === 'uc',
                'bg-yellow-500 text-black': result.disk_type === 'xunlei',
                'bg-gray-500 text-white': !result.disk_type
              }"
            >
              <img v-if="result.disk_type === 'quark'" src="/favicon-quark.ico" alt="" class="w-3.5 h-3.5" />
              <img v-else-if="result.disk_type === 'baidu'" src="/favicon-baidu.ico" alt="" class="w-3.5 h-3.5" />
              <img v-else-if="result.disk_type === 'xunlei'" src="/xunlei.png" alt="" class="w-3.5 h-3.5" />
              {{ result.disk_type_label }}
            </span>
          </div>
          
          <div class="flex items-center justify-between">
            <div class="flex items-center text-gray-400 text-sm">
              <span class="mr-4">来源: {{ result.source }}</span>
            </div>
            
            <div class="search-result-actions">
              <button
                @click="extractLink(result.url)"
                :disabled="extractingUrls.has(result.url)"
                :class="[
                  'px-4 py-2 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-white',
                  result.disk_type === 'quark' 
                    ? 'bg-[#8B5CF6] hover:bg-[#7C3AED] active:bg-[#6D28D9]' 
                    : result.disk_type === 'xunlei'
                    ? 'bg-[#00A4FF] hover:bg-[#0094E8] active:bg-[#0084D0]'
                    : 'bg-[#06A7FF] hover:bg-[#0095E8] active:bg-[#0085D0]'
                ]"
              >
                <span v-if="extractingUrls.has(result.url)" class="flex items-center">
                  <div class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  提取中...
                </span>
                <span v-else class="flex items-center">
                  <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                  提取链接
                </span>
              </button>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="totalPages > 1" class="flex justify-center items-center space-x-4 mt-8">
          <button
            @click="handlePageChange(currentPage - 1)"
            :disabled="currentPage === 1"
            class="px-4 py-2 bg-gray-700 hover:bg-gray-600 active:bg-gray-800 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            上一页
          </button>
          <span class="text-white">
            第 {{ currentPage }} 页，共 {{ totalPages }} 页
          </span>
          <button
            @click="handlePageChange(currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="px-4 py-2 bg-gray-700 hover:bg-gray-600 active:bg-gray-800 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            下一页
          </button>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else-if="searchQuery" class="text-center py-16">
        <svg class="w-16 h-16 text-gray-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-gray-400 mt-4">没有找到相关结果</p>
      </div>
    </div>

    <!-- 全局loading遮罩层 -->
    <div v-if="globalLoading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-gray-800 rounded-lg p-8 max-w-sm w-full mx-4">
        <div class="flex flex-col items-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p class="text-white text-lg font-medium">正在提取链接...</p>
          <p class="text-gray-400 text-sm mt-2">请稍等，这可能需要一些时间</p>
        </div>
      </div>
    </div>

    <!-- 搜索验证码弹窗 -->
    <div v-if="showSearchCaptchaModal" class="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div class="bg-gray-800 rounded-lg max-w-sm w-full shadow-2xl border border-gray-700">
        <div class="flex justify-between items-center px-5 py-4 border-b border-gray-700">
          <h3 class="text-base font-bold text-white flex items-center">
            <svg class="h-5 w-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5-6l-3 1m0 0l-3-1m3 1v6m-7 8h8a2 2 0 002-2v-7a2 2 0 00-2-2h-8a2 2 0 00-2 2v7a2 2 0 002 2z"/>
            </svg>
            安全验证
          </h3>
          <button @click="closeSearchCaptchaModal" class="text-gray-400 hover:text-white transition-colors">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <form @submit.prevent="submitSearchCaptcha" class="p-5">
          <p class="text-gray-300 text-sm mb-4">{{ searchCaptchaError || '请输入验证码后继续搜索' }}</p>

          <div class="flex items-center gap-3 mb-4">
            <div class="bg-white rounded-lg p-2 flex-1 h-16 flex items-center justify-center">
              <img
                v-if="searchCaptchaImage"
                :src="searchCaptchaImage"
                alt="搜索验证码"
                class="max-h-full max-w-full"
              />
              <div v-else class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
            <button
              type="button"
              @click="refreshSearchCaptcha"
              :disabled="captchaSubmitting"
              class="w-11 h-11 rounded-lg bg-gray-700 hover:bg-gray-600 text-white flex items-center justify-center transition-colors disabled:opacity-50"
              title="换一张"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v6h6M20 20v-6h-6M5 19A9 9 0 0119 5m0 0h-5m5 0v5"/>
              </svg>
            </button>
          </div>

          <input
            v-model.trim="searchCaptchaAnswer"
            type="text"
            maxlength="6"
            autocomplete="off"
            class="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:border-blue-500 mb-4 tracking-widest uppercase"
            placeholder="输入验证码"
          />

          <button
            type="submit"
            :disabled="captchaSubmitting || !searchCaptchaAnswer"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <span v-if="captchaSubmitting" class="flex items-center">
              <div class="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
              验证中...
            </span>
            <span v-else>继续搜索</span>
          </button>
        </form>
      </div>
    </div>

    <!-- 转存成功弹窗 - 移动端 -->
    <div v-if="showTransferModal && isMobile" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div class="bg-gray-800 rounded-lg max-w-sm w-full max-h-[90vh] overflow-y-auto">
        <!-- 弹窗头部 -->
        <div class="flex justify-between items-center px-4 py-3 border-b border-gray-700">
          <h3 class="text-base font-bold text-white flex items-center">
            <svg class="h-5 w-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            提取成功
          </h3>
          <button @click="closeTransferModal" class="text-gray-400 hover:text-white transition-colors">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <!-- 弹窗内容 -->
        <div class="p-4">
          <!-- 友情提示 -->
          <div class="mb-3 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg p-2.5 shadow-lg">
            <div class="flex items-start">
              <svg class="w-4 h-4 text-white mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <p class="text-white text-xs leading-relaxed">
                热门资源夸克易和谐，如果出现<span class="font-semibold underline">缺集、空文件</span>等情况都是被和谐了，请换一个资源即可。
              </p>
            </div>
          </div>

          <!-- 提示信息 -->
          <div class="bg-blue-600 text-white px-3 py-2 rounded-lg mb-3 flex items-center text-xs">
            <svg class="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>资源将在5分钟后失效，请及时保存到网盘!</span>
          </div>

          <!-- 分享链接 -->
          <div class="mb-3">
            <label class="block text-gray-400 text-xs mb-1">分享链接：</label>
            <div class="flex items-center space-x-2">
              <input type="text" :value="transferResult.share_url" readonly class="flex-1 bg-gray-700 text-white px-2 py-1.5 rounded border border-gray-600 text-xs focus:outline-none" />
              <button @click="copyShareUrl" class="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded transition-colors duration-200 flex items-center text-xs">
                <span v-if="copied" class="flex items-center">
                  <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                  已复制
                </span>
                <span v-else>复制</span>
              </button>
            </div>
          </div>

          <!-- 操作按钮 -->
          <div class="flex space-x-2">
            <button @click="openLink(transferResult.share_url)" class="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded transition-colors duration-200 flex items-center justify-center text-sm">
              <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
              </svg>
              打开链接
            </button>
            <button @click="closeTransferModal" class="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded transition-colors duration-200 text-sm">关闭</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 转存成功弹窗 - PC端（强制扫码） -->
    <div v-if="showTransferModal && !isMobile" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-gray-800 rounded-xl max-w-md w-full shadow-2xl">
        <!-- 弹窗头部 -->
        <div class="flex justify-between items-center px-6 py-4 border-b border-gray-700">
          <h3 class="text-lg font-bold text-white flex items-center">
            <svg class="h-6 w-6 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            提取成功
          </h3>
          <button @click="closeTransferModal" class="text-gray-400 hover:text-white transition-colors">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <!-- 弹窗内容 -->
        <div class="p-6">
          <!-- 友情提示 -->
          <div class="mb-4 bg-gradient-to-r from-yellow-600 to-orange-600 rounded-lg p-3 shadow-lg">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-white mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              <p class="text-white text-sm leading-relaxed">
                热门资源夸克易和谐，如果出现<span class="font-semibold underline">缺集、空文件</span>等情况都是被和谐了，请换一个资源即可。
              </p>
            </div>
          </div>

          <!-- PC端扫码提示 -->
          <div class="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 rounded-lg mb-4 text-center">
            <div class="flex items-center justify-center mb-1">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
              </svg>
              <span class="font-semibold">请使用手机扫码转存</span>
            </div>
            <p class="text-xs text-blue-100">资源将在5分钟后失效，请及时保存到网盘!</p>
          </div>
          
          <!-- 大二维码 -->
          <div class="mb-4">
            <div class="bg-white p-4 rounded-xl flex justify-center shadow-inner">
              <div class="text-center">
                <div v-if="qrCodeUrl" class="w-48 h-48 flex items-center justify-center rounded-lg mx-auto">
                  <img :src="qrCodeUrl" alt="分享链接二维码" class="w-full h-full object-contain" />
                </div>
                <div v-else class="w-48 h-48 flex items-center justify-center rounded-lg mx-auto bg-gray-100">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
                <p class="text-gray-600 text-sm mt-3 font-medium">打开夸克/百度/迅雷APP或微信/浏览器扫码转存</p>
              </div>
            </div>
          </div>

          <!-- 百度网盘提示 -->
          <div class="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div class="flex items-start">
              <svg class="w-4 h-4 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <p class="text-blue-700 text-xs leading-relaxed">
                <span class="font-semibold">百度网盘用户注意：</span>建议使用<span class="font-semibold text-blue-800">微信扫码</span>打开链接，可自动填入提取码。直接用百度APP扫码可能无法自动填入提取码。
              </p>
            </div>
          </div>

          <!-- 关闭按钮 -->
          <button @click="closeTransferModal" class="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-3 rounded-lg transition-colors duration-200 text-sm font-medium">关闭</button>
        </div>
      </div>
    </div>

    <!-- 转存错误提示 -->
    <div v-if="transferError" class="fixed top-4 right-4 bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg z-50">
      <div class="flex items-center space-x-2">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
        <span>{{ transferError }}</span>
      </div>
      <button
        @click="setTransferError('')"
        class="absolute top-2 right-2 text-white hover:text-gray-200 transition-colors"
      >
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import QRCode from 'qrcode'
import { filterBlockedResources } from '../utils/resourceFilter'

export default {
  name: 'Search',
  data() {
    return {
      searchQuery: '',
      results: [],
      loading: false,
      error: '',
      currentPage: 1,
      totalPages: 1,
      totalResults: 0,
      selectedDiskType: '',
      extractingUrls: new Set(),
      showTransferModal: false,
      transferResult: null,
      transferError: '',
      copied: false,
      qrCodeUrl: '',
      // 添加全局loading状态
      globalLoading: false,
      isMobile: false,
      showSearchCaptchaModal: false,
      searchCaptchaToken: '',
      searchCaptchaImage: '',
      searchCaptchaAnswer: '',
      searchCaptchaError: '',
      captchaSubmitting: false,
      pendingSearchCaptcha: {
        page: 1,
        diskType: ''
      }
    }
  },
  mounted() {
    // 从URL参数获取搜索关键词
    const query = this.$route.query.q || ''
    this.searchQuery = query

    if (query) {
      this.performSearch(1)
    }
    
    this.checkMobile()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  watch: {
    transferResult(newVal) {
      if (newVal && newVal.share_url) {
        this.$nextTick(() => {
          this.generateQRCode(newVal.share_url)
        })
      }
    }
  },
  methods: {
    checkMobile() {
      // 使用视口宽度判断，768px以下为移动端
      this.isMobile = window.innerWidth < 768
    },
    handleResize() {
      this.checkMobile()
    },
    goBack() {
      // 直接返回到主页
      this.$router.push('/')
    },
    isSearchCaptchaResponse(responseData) {
      return responseData && responseData.code === 429 && responseData.data?.captcha_required
    },
    showSearchCaptcha(responseData, page, diskType) {
      this.pendingSearchCaptcha = { page, diskType }
      this.searchCaptchaToken = responseData.data.captcha_token || ''
      this.searchCaptchaImage = responseData.data.captcha_image || ''
      this.searchCaptchaAnswer = ''
      this.searchCaptchaError = responseData.message || '请输入验证码后继续搜索'
      this.showSearchCaptchaModal = true
      this.error = ''
    },
    async refreshSearchCaptcha() {
      try {
        this.captchaSubmitting = true
        const response = await axios.get('/api/interface/captcha/search')
        if (response.data.code === 200 && response.data.data?.captcha_token) {
          this.searchCaptchaToken = response.data.data.captcha_token
          this.searchCaptchaImage = response.data.data.captcha_image || ''
          this.searchCaptchaAnswer = ''
          this.searchCaptchaError = '请输入新的验证码'
        } else {
          this.searchCaptchaError = response.data.message || '验证码刷新失败'
        }
      } catch (error) {
        console.error('刷新搜索验证码失败:', error)
        this.searchCaptchaError = '验证码刷新失败，请稍后重试'
      } finally {
        this.captchaSubmitting = false
      }
    },
    async submitSearchCaptcha() {
      if (!this.searchCaptchaAnswer.trim()) {
        this.searchCaptchaError = '请输入验证码'
        return
      }

      this.captchaSubmitting = true
      try {
        await this.performSearchWithDiskType(
          this.pendingSearchCaptcha.page,
          this.pendingSearchCaptcha.diskType,
          {
            token: this.searchCaptchaToken,
            answer: this.searchCaptchaAnswer.trim()
          }
        )
      } finally {
        this.captchaSubmitting = false
      }
    },
    closeSearchCaptchaModal() {
      this.showSearchCaptchaModal = false
      this.searchCaptchaToken = ''
      this.searchCaptchaImage = ''
      this.searchCaptchaAnswer = ''
      this.searchCaptchaError = ''
    },
    async performSearch(page = 1) {
      return this.performSearchWithDiskType(page, this.selectedDiskType)
    },
    async performSearchWithDiskType(page = 1, diskType = '', captchaPayload = null) {
      if (!this.searchQuery.trim()) return
      
      this.loading = true
      this.error = ''
      
      try {
        const params = new URLSearchParams({
          keyword: this.searchQuery.trim(),
          page: page.toString(),
          pageSize: '20'
        })
        
        if (diskType) {
          params.append('disk_type', diskType)
        }

        if (captchaPayload?.token && captchaPayload?.answer) {
          params.append('captcha_token', captchaPayload.token)
          params.append('captcha_answer', captchaPayload.answer)
        }
        
        const response = await axios.get(`/api/interface/search?${params}`)
        
        if (response.data.code === 200) {
          if (captchaPayload) {
            this.closeSearchCaptchaModal()
          }
          const responseList = response.data.data.list || []
          this.results = filterBlockedResources(responseList)
          this.totalResults = response.data.data.pagination?.total || 0
          this.totalPages = response.data.data.pagination?.totalPages || 1
          this.currentPage = page
        } else {
          this.error = response.data.message || '搜索失败'
          this.results = []
        }
      } catch (error) {
        console.error('搜索失败:', error)
        const responseData = error.response?.data
        if (this.isSearchCaptchaResponse(responseData)) {
          this.showSearchCaptcha(responseData, page, diskType)
          return
        }
        this.error = '网络错误，请稍后重试'
        this.results = []
      } finally {
        this.loading = false
      }
    },
    handleSearch(e) {
      e.preventDefault()
      if (this.searchQuery.trim()) {
        // 更新URL参数
        this.$router.push({ 
          path: '/search', 
          query: { q: this.searchQuery.trim() } 
        })
        this.performSearch(1)
      }
    },
    handleDiskTypeChange() {
      this.performSearch(1)
    },
    handleDiskTypeSelect(diskType) {
      this.selectedDiskType = diskType
      this.performSearch(1)
    },
    handlePageChange(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.performSearch(page)
      }
    },
    async copyToClipboard(url) {
      try {
        await navigator.clipboard.writeText(url)
        alert('链接已复制到剪贴板')
      } catch (error) {
        console.error('复制失败:', error)
      }
    },
    openLink(url) {
      window.open(url, '_blank')
    },
    async extractLink(encryptedUrl) {
      try {
        // 开始全局loading
        this.globalLoading = true
        this.extractingUrls.add(encryptedUrl)
        this.transferError = ''
        this.transferResult = null
        this.qrCodeUrl = ''
        
        const response = await axios.post(`/api/transfer-share/transfer-share`, {
          encrypted_url: encryptedUrl
        })
        
        if (response.data.code === 200) {
          this.transferResult = {
            share_url: response.data.data.share_url,
            original_url: response.data.data.original_url,
            passcode: response.data.data.passcode,
            file_info: response.data.data.file_info
          }
          this.showTransferModal = true
        } else {
          // 直接显示后端返回的错误信息
          this.transferError = response.data.message || '链接提取失败'
        }
      } catch (error) {
        console.error('提取链接失败:', error)
        // 区分网络错误和服务器返回的错误
        if (error.response && error.response.data) {
          // 服务器返回了错误响应，显示具体的错误信息
          this.transferError = error.response.data.message || '链接提取失败'
        } else if (error.request) {
          // 请求发送失败，显示网络错误
          this.transferError = '网络错误，请检查网络连接后重试'
        } else {
          // 其他错误
          this.transferError = '链接提取失败，请稍后重试'
        }
      } finally {
        // 结束全局loading
        this.globalLoading = false
        this.extractingUrls.delete(encryptedUrl)
      }
    },
    async copyShareUrl() {
      if (!this.transferResult) return
      
      try {
        await navigator.clipboard.writeText(this.transferResult.share_url)
        this.copied = true
        setTimeout(() => {
          this.copied = false
        }, 2000)
      } catch (error) {
        console.error('复制失败:', error)
      }
    },
    closeTransferModal() {
      this.showTransferModal = false
      this.transferResult = null
      this.transferError = ''
      this.copied = false
      this.qrCodeUrl = ''
    },
    setTransferError(error) {
      this.transferError = error
    },
    async generateQRCode(url) {
      try {
        // PC端生成更大的二维码
        const size = this.isMobile ? 128 : 256
        this.qrCodeUrl = await QRCode.toDataURL(url, {
          width: size,
          height: size,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        })
      } catch (error) {
        console.error('生成二维码失败:', error)
      }
    }
  }
}
</script>
